import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Wand2, Check, Globe } from 'lucide-react';

const Swatch = ({ hex, selected, onClick, testid }) => (
  <button type="button" onClick={onClick} title={hex} data-testid={testid}
    className={`relative h-9 w-9 rounded-lg border-2 transition ${selected ? 'border-zinc-900 scale-105' : 'border-white shadow ring-1 ring-zinc-200'}`}
    style={{ backgroundColor: hex }}>
    {selected && <Check className="h-4 w-4 text-white absolute inset-0 m-auto drop-shadow" />}
  </button>
);

export const BrandAnalyzer = ({ form, onApply, onBrandChange }) => {
  const brand = form.checkout_ui?.brand || {};
  const defaultUrl = (form.custom_domains && form.custom_domains[0]) || form.woo_url || form.shopify_domain || '';
  const [url, setUrl] = useState(brand.source_url || defaultUrl);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [accent, setAccent] = useState('');

  const analyze = async () => {
    if (!url.trim()) { toast.error("Indique l'URL de ta boutique"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/stores/${form.id}/brand-analyze`, { url: url.trim() });
      setResult(data); setAccent(data.accent_color || '');
      toast.success('Boutique analysée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse impossible");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!result) return;
    onApply({
      accent_color: accent || result.accent_color || form.accent_color,
      logo_url: result.logo_url || form.logo_url,
      checkout_ui: {
        ...form.checkout_ui,
        template: 'brand',
        favicon_url: result.favicon_url || form.checkout_ui?.favicon_url || '',
        brand: {
          ...brand,
          source_url: result.url,
          accent: accent || result.accent_color,
          secondary: result.secondary_color,
          background: result.background_color,
          text: result.text_color,
          palette: result.palette,
          font_family: result.font_family,
          font_url: result.font_url,
          analyzed_at: new Date().toISOString(),
        },
      },
    });
    toast.success('Template sur mesure appliqué — pense à Enregistrer');
  };

  const setB = (patch) => onBrandChange({ ...brand, ...patch });
  const swatches = result ? Array.from(new Set([result.accent_color, ...(result.palette || []), ...(result.logo_colors || [])].filter(Boolean))) : [];

  return (
    <div className="rounded-lg border-2 border-dashed border-zinc-200 p-3 space-y-3" data-testid="brand-analyzer">
      <div>
        <Label className="text-xs font-bold text-zinc-800 flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5" /> TEMPLATE SUR MESURE</Label>
        <p className="text-[11px] text-zinc-400 mt-0.5">Colle le lien de ta boutique : on récupère logo, couleurs et police pour construire un checkout à ton image.</p>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Globe className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://maboutique.com" className="h-9 pl-9 text-sm" data-testid="brand-url-input"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); analyze(); } }} />
        </div>
        <Button type="button" onClick={analyze} disabled={busy} className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white text-xs px-3" data-testid="brand-analyze-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Analyser'}
        </Button>
      </div>

      {result && (
        <div className="space-y-3 bg-zinc-50 rounded-lg p-3 border border-zinc-200" data-testid="brand-analysis-result">
          <div className="flex items-center gap-3">
            <div className="h-14 w-24 rounded-md border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
              {result.logo_url ? <img src={result.logo_url} alt="logo" className="max-h-12 max-w-[88px] object-contain" data-testid="brand-result-logo" /> : <span className="text-[10px] text-zinc-400">Pas de logo</span>}
            </div>
            <div className="min-w-0 text-xs">
              <div className="font-semibold truncate" data-testid="brand-result-name">{result.site_name || result.url}</div>
              <div className="text-zinc-500 truncate">Police : <b>{result.font_family || 'système'}</b></div>
              <div className="text-zinc-500">Fond <span className="inline-block h-3 w-3 rounded-sm border align-middle" style={{ backgroundColor: result.background_color }} /> · Texte <span className="inline-block h-3 w-3 rounded-sm border align-middle" style={{ backgroundColor: result.text_color }} /></div>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Couleur d'accent (bouton Payer)</div>
            <div className="flex flex-wrap gap-2" data-testid="brand-swatches">
              {swatches.map((hx, i) => <Swatch key={hx} hex={hx} selected={accent === hx} onClick={() => setAccent(hx)} testid={`brand-swatch-${i}`} />)}
              <Input type="color" value={accent || '#111111'} onChange={(e) => setAccent(e.target.value)} className="h-9 w-9 p-0.5 rounded-lg cursor-pointer" data-testid="brand-accent-custom" />
            </div>
          </div>
          <Button type="button" onClick={apply} className="w-full h-9 text-xs text-white" style={{ backgroundColor: accent || '#111' }} data-testid="brand-apply-btn">
            <Check className="h-3.5 w-3.5 mr-1" /> Appliquer le template sur mesure
          </Button>
        </div>
      )}

      {form.checkout_ui?.template === 'brand' && (
        <div className="space-y-2.5 pt-2 border-t border-zinc-200" data-testid="brand-options">
          <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Réglages du template</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-zinc-600">Fond de page</Label>
              <div className="flex gap-1.5 mt-1">
                <Input type="color" value={brand.background || '#f6f5f1'} onChange={(e) => setB({ background: e.target.value })} className="h-8 w-10 p-0.5" data-testid="brand-bg-color" />
                <Input value={brand.background || ''} onChange={(e) => setB({ background: e.target.value })} placeholder="#f6f5f1" className="h-8 text-xs font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-zinc-600">Police</Label>
              <Input value={brand.font_family || ''} onChange={(e) => setB({ font_family: e.target.value })} placeholder="Système" className="h-8 text-xs mt-1" data-testid="brand-font" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-zinc-600">Mode de livraison</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="brand-delivery-mode">
              {[['standard', 'Standard (délai estimé)', 'Colis : estimation + compte à rebours'], ['instant', 'Instantanée', 'Produits digitaux / services : accès immédiat']].map(([k, l, h]) => (
                <button key={k} type="button" onClick={() => setB({ delivery_mode: k })} aria-pressed={(brand.delivery_mode || 'standard') === k} className={`text-left rounded-md border p-2 ${(brand.delivery_mode || 'standard') === k ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 hover:bg-zinc-50'}`} data-testid={`brand-delivery-${k}`}>
                  <div className="text-xs font-semibold">{l}</div><div className={`text-[10px] ${(brand.delivery_mode || 'standard') === k ? 'text-zinc-300' : 'text-zinc-500'}`}>{h}</div>
                </button>
              ))}
            </div>
          </div>
          {brand.delivery_mode === 'instant' ? (
            <p className="text-[11px] text-zinc-500" data-testid="brand-instant-hint">Le checkout affiche « Livraison instantanée · accès immédiat après le paiement » à la place de l'estimation en jours ; le compte à rebours d'expédition est masqué.</p>
          ) : (<>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-700">Compte à rebours « expédition aujourd'hui »</Label>
            <Switch checked={brand.show_countdown !== false} onCheckedChange={(v) => setB({ show_countdown: v })} data-testid="brand-toggle-countdown" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px] text-zinc-600">Heure limite</Label>
              <Input type="number" min={0} max={23} value={brand.cutoff_hour ?? 17} onChange={(e) => setB({ cutoff_hour: +e.target.value })} className="h-8 text-xs mt-1" data-testid="brand-cutoff" />
            </div>
            <div>
              <Label className="text-[11px] text-zinc-600">Livraison min (j)</Label>
              <Input type="number" min={0} value={brand.delivery_min_days ?? 3} onChange={(e) => setB({ delivery_min_days: +e.target.value })} className="h-8 text-xs mt-1" data-testid="brand-eta-min" />
            </div>
            <div>
              <Label className="text-[11px] text-zinc-600">Livraison max (j)</Label>
              <Input type="number" min={0} value={brand.delivery_max_days ?? 7} onChange={(e) => setB({ delivery_max_days: +e.target.value })} className="h-8 text-xs mt-1" data-testid="brand-eta-max" />
            </div>
          </div>
          </>)}
        </div>
      )}
    </div>
  );
};
